"use client";

import { FunctionComponent, ReactNode } from "react";
import { Adornments, Body, Label, Wrapper } from "./chip.styles";
import { ButtonLink, TailwindColors } from "@components/common/types/general";
import { Link } from "../link/link";

export interface ChipProps {
    /**
     * Color of the chip.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Use a full tone background/border color. `fullTone={true}` will only be background color if `outline` is `false` or not set.
     */
    fullTone?: boolean;

    /**
     * Remove background color and only display outline.
     */
    outline?: boolean;

    /**
     * Left adornments of the chip.
     */
    leftAdornments?: ReactNode | string;

    /**
     * Right adornments of the chip.
     */
    rightAdornments?: ReactNode | string;

    /**
     * Caption of the chip.
     */
    label?: string | ReactNode;

    /**
     * Border radius of the chip.
     */
    rounded?: "none" | "md" | "lg" | "full";

    /**
     * Size of the chip.
     */
    size?: "xs" | "sm" | "md" | "lg" | "xl";

    /**
     * Transform the chip to a link.
     */
    link?: ButtonLink;

    /**
     * A callback function which will be fired on click. This will also transform the chip to a button
     */
    onClick?: () => void;

    /**
     * Class name of the chip.
     */
    className?: string;
}

export const Chip: FunctionComponent<ChipProps> = ({
    color,
    fullTone = false,
    outline = false,
    leftAdornments,
    rightAdornments,
    label,
    rounded = "none",
    size = "md",
    link,
    onClick,
    className,
}) => {
    const CustomTag = link ? Link : onClick ? "button" : "div";
    const customProps = link ? { ...link } : onClick ? { onClick, type: "button", tabIndex: 0 } : {};

    // todo: add styles for button and link chip

    return (
        <Wrapper className={className}>
            <Body as={CustomTag} {...customProps} color={color} fullTone={fullTone} outline={outline} rounded={rounded} size={size} label={label}>
                {leftAdornments && <Adornments size={size}>{leftAdornments}</Adornments>}
                {label && <Label>{label}</Label>}
                {rightAdornments && <Adornments size={size}>{rightAdornments}</Adornments>}
            </Body>
        </Wrapper>
    );
};
